import React, { useEffect, useId, useRef, useState } from 'react';

const SENSOR_FIELD_META = {
  barcodeInput: {
    buttonLabel: 'Scan Barcode',
    emptyHint: 'Scan a barcode or type the value manually.',
    formats: ['code_128', 'code_39', 'code_93', 'codabar', 'ean_13', 'ean_8', 'itf', 'upc_a', 'upc_e']
  },
  qrCodeInput: {
    buttonLabel: 'Scan QR Code',
    emptyHint: 'Scan a QR code or type the value manually.',
    formats: ['qr_code']
  },
  nfcInput: {
    buttonLabel: 'Read NFC',
    emptyHint: 'Tap an NFC tag or type the value manually.'
  }
};

const stopStream = (stream) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
};

const decodeNfcRecord = (record) => {
  if (!record) return '';

  if (record.recordType === 'text' && record.data) {
    const bytes = new Uint8Array(record.data.buffer.slice(record.data.byteOffset, record.data.byteOffset + record.data.byteLength));
    if (!bytes.length) return '';
    const languageLength = bytes[0] & 0x3f;
    return new TextDecoder().decode(bytes.slice(1 + languageLength)).trim();
  }

  if ((record.recordType === 'url' || record.mediaType === 'text/uri-list') && record.data) {
    return new TextDecoder().decode(record.data).trim();
  }

  if (record.data) {
    return new TextDecoder().decode(record.data).trim();
  }

  return `${record.recordType || 'nfc_record'}`.trim();
};

const joinNfcPayload = (records = []) =>
  records
    .map((record) => decodeNfcRecord(record))
    .filter(Boolean)
    .join('\n')
    .trim();

const SmartSensorField = ({
  type,
  fieldKey,
  value,
  label,
  placeholder = '',
  textSizeClass = 'text-sm',
  alignClass = 'text-left',
  onValueChange,
  readOnly = false
}) => {
  const meta = SENSOR_FIELD_META[type] || SENSOR_FIELD_META.barcodeInput;
  const generatedId = useId();
  const inputId = `${fieldKey || generatedId}-sensor-input`;
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [readingNfc, setReadingNfc] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const nfcAbortRef = useRef(null);
  const onValueChangeRef = useRef(onValueChange);

  useEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  useEffect(() => () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    stopStream(streamRef.current);
    streamRef.current = null;
    if (nfcAbortRef.current) {
      nfcAbortRef.current.abort();
      nfcAbortRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!scannerOpen || type === 'nfcInput' || readOnly) return undefined;

    let cancelled = false;

    const closeScanner = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      stopStream(streamRef.current);
      streamRef.current = null;
    };

    const run = async () => {
      if (!window.BarcodeDetector) {
        setScanError('This browser does not support camera code scanning.');
        setScannerOpen(false);
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        setScanError('Camera access is not available on this device.');
        setScannerOpen(false);
        return;
      }

      setScanError('');
      setScanMessage('Starting camera...');

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        });

        if (cancelled) {
          stopStream(stream);
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        setScanMessage(type === 'qrCodeInput' ? 'Point the camera at a QR code.' : 'Point the camera at a barcode.');

        let detector;
        try {
          detector = new window.BarcodeDetector({ formats: meta.formats });
        } catch (error) {
          detector = new window.BarcodeDetector();
        }

        const detectFrame = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const results = await detector.detect(videoRef.current);
            const match = results.find((item) => `${item?.rawValue || ''}`.trim());
            if (match) {
              onValueChangeRef.current(match.rawValue.trim());
              setScanMessage(type === 'qrCodeInput' ? 'QR code captured.' : 'Barcode captured.');
              setScannerOpen(false);
              closeScanner();
              return;
            }
          } catch (error) {
            setScanError(error?.message || 'Code scan failed.');
            setScannerOpen(false);
            closeScanner();
            return;
          }

          rafRef.current = requestAnimationFrame(detectFrame);
        };

        rafRef.current = requestAnimationFrame(detectFrame);
      } catch (error) {
        setScanError(error?.message || 'Unable to access the camera.');
        setScannerOpen(false);
        closeScanner();
      }
    };

    run();

    return () => {
      cancelled = true;
      closeScanner();
    };
  }, [meta.formats, readOnly, scannerOpen, type]);

  const handleNfcRead = async () => {
    if (readOnly) return;

    if (!window.NDEFReader) {
      setScanError('This browser does not support Web NFC.');
      return;
    }

    if (nfcAbortRef.current) {
      nfcAbortRef.current.abort();
      nfcAbortRef.current = null;
    }

    const controller = new AbortController();
    nfcAbortRef.current = controller;

    try {
      const reader = new window.NDEFReader();
      setReadingNfc(true);
      setScanError('');
      setScanMessage('Hold an NFC tag near this device.');

      reader.onreadingerror = () => {
        setScanError('Unable to read this NFC tag.');
      };

      reader.onreading = (event) => {
        const payload = joinNfcPayload(event?.message?.records || []);
        onValueChangeRef.current(payload || 'nfc_tag_detected');
        setScanMessage('NFC tag captured.');
        setReadingNfc(false);
        controller.abort();
        nfcAbortRef.current = null;
      };

      await reader.scan({ signal: controller.signal });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setReadingNfc(false);
      setScanError(error?.message || 'Unable to start NFC reading.');
      nfcAbortRef.current = null;
    }
  };

  const helperText = scanError || scanMessage || meta.emptyHint;
  const helperClass = scanError ? 'text-rose-600' : 'text-slate-500';

  return (
    <div className="mb-3">
      <label htmlFor={inputId} className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <input
          id={inputId}
          data-field-key={fieldKey}
          name={fieldKey}
          type="text"
          value={value || ''}
          placeholder={placeholder || ''}
          readOnly={readOnly}
          onChange={(event) => onValueChange(event.target.value)}
          className={`w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${textSizeClass}`}
        />
        <button
          type="button"
          data-export-hide="true"
          disabled={readOnly || readingNfc}
          onClick={() => {
            setScanError('');
            if (type === 'nfcInput') {
              handleNfcRead();
              return;
            }
            setScannerOpen(true);
          }}
          className="inline-flex shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {readingNfc ? 'Waiting...' : meta.buttonLabel}
        </button>
      </div>
      <p className={`mt-2 text-xs ${helperClass}`}>{helperText}</p>

      {scannerOpen && type !== 'nfcInput' && (
        <div
          data-export-hide="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4"
          onClick={() => setScannerOpen(false)}
        >
          <div className="w-full max-w-xl rounded-2xl bg-white p-4 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-[#232753]">{meta.buttonLabel}</h3>
                <p className="mt-1 text-sm text-slate-500">{scanMessage || meta.emptyHint}</p>
              </div>
              <button
                type="button"
                onClick={() => setScannerOpen(false)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
            <div className="mt-4 overflow-hidden rounded-2xl bg-slate-950">
              <video ref={videoRef} muted playsInline className="h-[320px] w-full object-cover" />
            </div>
            {scanError && <p className="mt-3 text-sm text-rose-600">{scanError}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartSensorField;
